'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import {
    ShieldCheck, Globe, CreditCard, ArrowRight, CheckCircle2,
    Zap, ChevronLeft, ShoppingCart, User
} from 'lucide-react';
import Link from 'next/link';

function BuyForm() {
    const searchParams = useSearchParams();
    const planFromUrl = searchParams.get('plan') || 'ANNUAL';

    const [step, setStep] = useState(1);
    const [loading, setLoading] = useState(false);
    const [prices, setPrices] = useState<any>({
        'ANNUAL': { label: '年度订阅', price: 999 },
        'PERMANENT': { label: '终身授权', price: 3999 }
    });
    const [formData, setFormData] = useState({
        domain: '',
        name: '',
        email: '',
        plan: planFromUrl
    });

    useEffect(() => {
        fetch('/api/public/configs/prices')
            .then(res => res.json())
            .then(data => {
                setPrices({
                    'ANNUAL': { label: '年度订阅', price: data.ANNUAL },
                    'PERMANENT': { label: '终身授权', price: data.PERMANENT }
                });
            })
            .catch(() => { });
    }, []);

    const handleCreateOrder = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.domain || !formData.email) return;

        setLoading(true);
        try {
            const res = await fetch('/api/public/order/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const data = await res.json();
            if (data.orderNo) {
                // 跳转到支付选择页或直接发起支付宝支付
                window.location.href = `/api/public/payment/alipay?orderNo=${data.orderNo}`;
            } else {
                alert(data.error || '创建订单失败');
            }
        } catch (error) {
            alert('系统繁忙，请稍后再试');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="max-w-xl mx-auto">
            <Link href="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-8 transition-colors group text-sm font-medium">
                <ChevronLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                返回首页
            </Link>

            <div className="bg-white rounded-[2.5rem] border border-slate-200/60 shadow-2xl p-8 md:p-12">
                <div className="flex items-center gap-3 mb-10">
                    <div className="w-12 h-12 bg-primary-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-primary-600/20">
                        <ShoppingCart className="w-6 h-6" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-black text-slate-900 tracking-tight">授权配置</h1>
                        <p className="text-sm text-slate-400">正在购买: {prices[formData.plan].label}</p>
                    </div>
                </div>

                <form onSubmit={handleCreateOrder} className="space-y-6">
                    <div>
                        <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                            授权域名
                            <span className="text-xs font-normal text-slate-400">(格式: domain.com，支付后不可更改)</span>
                        </label>
                        <div className="relative group">
                            <Globe className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-primary-500 transition-colors" />
                            <input
                                required
                                type="text"
                                placeholder="请输入您要绑定的正式域名"
                                value={formData.domain}
                                onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                                className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all font-medium"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">您的称呼</label>
                            <div className="relative group">
                                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-300 group-focus-within:text-primary-500 transition-colors" />
                                <input
                                    required
                                    type="text"
                                    placeholder="姓名 / 公司名"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    className="w-full pl-11 pr-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all font-medium hover:bg-slate-100/50"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-bold text-slate-700 mb-2">接收邮箱</label>
                            <input
                                required
                                type="email"
                                placeholder="用于接收授权凭证"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                className="w-full px-4 py-4 bg-slate-50 border border-slate-100 rounded-2xl focus:bg-white focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all font-medium hover:bg-slate-100/50"
                            />
                        </div>
                    </div>

                    <div className="p-6 bg-primary-50 rounded-3xl border border-primary-100 flex items-center justify-between">
                        <div>
                            <p className="text-xs font-bold text-primary-600 uppercase tracking-wider mb-1">支付金额</p>
                            <div className="flex items-baseline gap-1">
                                <span className="text-slate-900 font-bold">¥</span>
                                <span className="text-3xl font-black text-slate-900 tracking-tighter">{prices[formData.plan].price.toLocaleString()}</span>
                            </div>
                        </div>
                        <div className="text-right">
                            <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-primary-100 rounded-full text-[10px] font-bold text-primary-600 h-fit">
                                <ShieldCheck className="w-3 h-3" />
                                正版安全保障
                            </span>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black text-lg hover:bg-slate-800 transition-all shadow-xl shadow-slate-900/10 active:scale-[0.98] flex items-center justify-center gap-3 disabled:opacity-70"
                    >
                        {loading ? '正在处理...' : (
                            <>
                                立即前往支付宝支付
                                <ArrowRight className="w-5 h-5" />
                            </>
                        )}
                    </button>

                    <p className="text-center text-[11px] text-slate-400 font-medium">
                        点击上方按钮代表您已阅读并同意《GeoCMS 软件授权协议》
                    </p>
                </form>
            </div>
        </div>
    );
}

export default function BuyPage() {
    return (
        <div className="min-h-screen bg-slate-50 font-sans py-20 px-6">
            <Suspense fallback={<div>Loading...</div>}>
                <BuyForm />
            </Suspense>
        </div>
    );
}
